import { IRawContractorGroupMember, TRawContractorGroupMember } from "@propely/propely-typings";
import { queryClient } from "@src/index";
import { DataCreator } from "@src/utils/dataCreator";
import { deleteDoc, doc, getDoc, getFirestore, setDoc, updateDoc } from "firebase/firestore";
import { useQueries, useQuery, UseQueryOptions } from "react-query";
import { loadParsedFirebaseCollection } from "../generic/firebaseLoaders";

export type IRawContractorGroupMemberWithContractorGroupID = IRawContractorGroupMember & { contractorGroupID: string };

export const createContractorGroupMember = (contractorGroupID: string, userID: string, admin: boolean) => {
  return setDoc(doc(getFirestore(), "contractorGroups", contractorGroupID, "members", userID), {
    admin,
    createdBy: DataCreator.getCreatedBy(),
  });
};

// Method to get a contractor group fetch key
export const useContractorGroupMemberKey = (contractorGroupID: string, memberID: string) => [
  "contractorGroups-member",
  { contractorGroupID, memberID },
];
export const useContractorGroupMembersKey = (contractorGroupID: string) => ["contractorGroups-members", { contractorGroupID }];

// Method to fetch members
const fetchContractorGroupMember = async (contractorGroupID: string, memberID: string): Promise<IRawContractorGroupMemberWithContractorGroupID> => {
  const member = await getDoc(doc(getFirestore(), "contractorGroups", contractorGroupID, "members", memberID));
  const parsedData = TRawContractorGroupMember.parse({
    id: member.id,
    ...member.data(),
  });
  return {
    ...parsedData,
    contractorGroupID,
  };
};

const fetchContractorGroupMembers = async (contractorGroupID: string) => {
  const { docs } = await loadParsedFirebaseCollection(`contractorGroups/${contractorGroupID}/members`, TRawContractorGroupMember);
  return docs;
};

export const useContractorGroupMember = (
  contractorGroupID: string,
  memberID: string,
  settings?: UseQueryOptions<IRawContractorGroupMember, unknown, IRawContractorGroupMember, any>
) => {
  const $member = useQuery(
    useContractorGroupMemberKey(contractorGroupID, memberID),
    () => fetchContractorGroupMember(contractorGroupID, memberID),
    settings
  );
  return {
    $member,
    member: $member.data,
  };
};

export const useContractorGroupMembers = (
  id: string,
  settings?: UseQueryOptions<IRawContractorGroupMember[], unknown, IRawContractorGroupMember[], any>
) => {
  const $members = useQuery(useContractorGroupMembersKey(id), () => fetchContractorGroupMembers(id), settings);
  return {
    $members,
    members: $members.data,
  };
};

export const useContractorGroupMemberList = (memberReferences: { contractorGroupID: string; memberID: string }[]) => {
  const $members = useQueries(
    memberReferences.map(({ contractorGroupID, memberID }) => ({
      queryKey: useContractorGroupMemberKey(contractorGroupID, memberID),
      queryFn: () => fetchContractorGroupMember(contractorGroupID, memberID),
    }))
  );

  const members = $members.map(($member) => $member.data).filter((data): data is IRawContractorGroupMemberWithContractorGroupID => Boolean(data));
  const isLoading = $members.some(($member) => $member.isLoading);

  return {
    $members,
    members,
    isLoading,
  };
};
export const updateContractorGroupMember = async (
  contractorID: string,
  memberID: string,
  data: Partial<IRawContractorGroupMember>,
  userID: string
) => {
  await updateDoc(doc(getFirestore(), "contractorGroups", contractorID, "members", memberID), {
    ...data,
    updatedBy: {
      userID,
      date: new Date(),
    },
  });
  await queryClient.invalidateQueries(useContractorGroupMembersKey(contractorID));
};

export const deleteContractorGroupMember = async (contractorID: string, memberID: string) => {
  await deleteDoc(doc(getFirestore(), "contractorGroups", contractorID, "members", memberID));
  await queryClient.invalidateQueries(useContractorGroupMembersKey(contractorID));
};
